/// FOURJS_START_COPYRIGHT(D,2021)
/// Property of Four Js*
/// (c) Copyright Four Js 2021, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("GestureService", ["ApplicationServiceBase", "ApplicationServiceFactory"], function (context, cls) {
  /**
   * Touch gesture management service
   * @class GestureService
   * @memberOf classes
   * @extends classes.ApplicationServiceBase
   */
  class GestureService extends cls.ApplicationServiceBase /** @lends classes.GestureService.prototype */ {
    /**
     * Swipe gesture service constructor
     * @param {classes.WidgetGroupBase|classes.WidgetBase} widget
     * @param {Object} swipeOptions
     * @param {Function} swipeOptions.moveCallback : custom function to execute during swipe
     * @param {boolean} swipeOptions.virtualDom : automatically add/remove hidden elements from DOM. False by default.
     * @param {string} swipeOptions.orientation : swipe direction. 'x' for horizontal, 'y' for vertical. Default is 'x'
     * @param {HTMLElement} swipeOptions.container : DOM element on which all events and css are attached. If not specified, we look for widget containerElement and element.
     * @param {boolean} swipeOptions.arrows : display arrows if true. False by default.
     * @param {boolean} swipeOptions.dots : display dots if true. False by default.
     * @param {boolean} swipeOptions.hideScrollbar : hide swipe corresponding scrollbar if true. True by default.
     * @param {boolean} swipeOptions.noSwipe : disable touch gesture if set to true. False by default
     */
    constructor(widget, swipeOptions) {
      super(widget, swipeOptions);
      /** @type {HTMLElement} */
      this.leftArrow = null;
      /** @type {HTMLElement} */
      this.rightArrow = null;
      /** @type {HTMLElement} */
      this.dots = null;
      /** @type {Number} */
      this.lastDirectionSign = 0;
      /** @type {Number} */
      this.lastHorizontalScrollPos = 0;
      /** @type {Function} */
      this.postponedScroll = null;
      /** @type {Number} */
      this.pastCleanedIndex = -1;
      /** @type {Object} */
      this.scrollTimerId = null;
      /** @type {Function} */
      this.focusHandler = null;
      /** @type {Function} */
      this.focusRestoredHandler = null;

      /** @type {Boolean} */
      this._hasNativeSmoothScroll = this._testSupportsSmoothScroll();
      this._viewPortWidth = window.innerWidth;
      /** @type {classes.WidgetGroupBase|classes.WidgetBase} */
      this._widget = widget;
      /** @type {Object} */

      this._swipeOpts = swipeOptions || {};
      /** @type {HTMLElement} */
      this._widgetContainer = this._swipeOpts.container || widget.getContainerElement() || widget.getElement();

      // defaults options
      if (!this._swipeOpts.orientation) {
        this._swipeOpts.orientation = "x";
      }
      if (typeof this._swipeOpts.hideScrollbar === "undefined") {
        this._swipeOpts.hideScrollbar = true;
      }

      if (this._swipeOpts.arrows) {
        this.addArrows();
      }
      if (this._swipeOpts.dots) {
        this.addDots();
      }

      this.enableSwipe();

      this._focusRestoredHandler = context.SessionService.getCurrent()
        .getCurrentApplication()
        .focus.when(context.constants.widgetEvents.focusRestored, this._onFocusRestored.bind(this));
    }

    /**
     * @inheritDoc
     */
    destroy() {
      this.disableSwipe();
      this._swipeOpts = null;
      super.destroy();
    }

    /**
     * Disable swipe from the widget
     */
    disableSwipe() {
      this.removeTouch();

      cls.DOMElementHelper.off(this._widgetContainer, "scroll.swipe");

      this._widgetContainer.classList.remove("noswipe");
      this._widgetContainer.classList.remove("swipeable");
      this._widget.removeClass("swiping");
      this._widgetContainer.classList.remove(this._swipeOpts.orientation);
      this._widgetContainer.classList.remove("hidden-scrollbar");

      this.removeArrows();
      this.removeDots();
      this._postponedScroll = null;
    }

    /**
     * Disable touch gestures (swipe only possible via arrows/dots)
     */
    removeTouch() {
      this._widgetContainer.classList.add("noswipe");
      cls.DOMElementHelper.off(this._widgetContainer, "touchstart.swipe");
      cls.DOMElementHelper.off(this._widgetContainer, "touchend.swipe");
      cls.DOMElementHelper.off(this._widgetContainer, "touchleave.swipe");
    }

    /**
     * Enable touch gestures
     */
    addTouch() {
      this._widgetContainer.classList.remove("noswipe");
      cls.DOMElementHelper.on(this._widgetContainer, "touchstart.swipe", this._onSwipeStart.bind(this));
      const swipeEnd = this._onSwipeEnd.bind(this);
      cls.DOMElementHelper.on(this._widgetContainer, "touchend.swipe", swipeEnd);
      cls.DOMElementHelper.on(this._widgetContainer, "touchleave.swipe", swipeEnd);
    }

    /**
     * Enable swipe
     * @returns {boolean}
     */
    enableSwipe() {
      if (!this._widget) {
        return;
      }

      cls.DOMElementHelper.toggleClass(this._widgetContainer, "hidden-scrollbar", this._swipeOpts.orientation);
      this._widgetContainer.classList.add(this._swipeOpts.orientation);
      this._widgetContainer.classList.remove("visibility-hidden");
      this._widgetContainer.classList.add("swipeable");

      this.removeTouch();
      if (!this._swipeOpts.noSwipe) {
        this.addTouch();
      }

      if (this._swipeOpts.virtualDom || this._swipeOpts.moveCallback) {
        cls.DOMElementHelper.on(this._widgetContainer, "scroll.swipe", this._onSwipeMove.bind(this, this._swipeOpts.moveCallback));
      }

      return true;
    }

    /**
     * Handler fired on swipe start. Add siblings elements to preview in DOM
     * @private
     */
    _onSwipeStart() {
      this._widget.addClasses("swiping");
      if (this._pagesInDomTimer && this._swipeOpts.virtualDom) {
        this._clearTimeout(this._pagesInDomTimer);
        this._cleanDom();
      }
      this._widget.emit(context.constants.widgetEvents.swipeStart);
    }

    /**
     * Handler fired during swipe. Update DOM if virtualDom option is on.
     * @param {Function} moveCallback
     * @param {object} evt
     * @private
     */
    _onSwipeMove(moveCallback, evt) {
      if (!this._widget.hasClass("swiping")) {
        return;
      }
      let distance = Math.round(this._widgetContainer.scrollLeft - this._lastHorizontalScrollPos);
      if (this._widget.isReversed()) {
        // manage RTL mode : inverse all
        distance *= -1;
      }
      const directionSign = Math.sign(distance);

      if (this._swipeOpts.virtualDom && directionSign !== this._lastDirectionSign && Math.abs(distance) > 2) {
        this._lastDirectionSign = directionSign;
        this._registerAnimationFrame(() => {
          this._updateDom(directionSign);
        });
      }

      if (moveCallback) {
        moveCallback.call(this._widget, null, distance, directionSign);
      }
    }

    /**
     * Handler fired on swipe end. Clean the DOM to display only current element
     * @private
     */
    _onSwipeEnd() {
      super._clearTimeout(this._pagesInDomTimer);

      // in a perfect world, this would not exist
      this._pagesInDomTimer = super._registerTimeout(() => {
        if (this._widget?._destroyed) {
          return;
        }
        this._widget?.removeClass("swiping");
        const index = this._cleanDom();
        this._lastHorizontalScrollPos = this._widgetContainer.scrollLeft;
        this._lastDirectionSign = 0;
        this._widget?.emit(context.constants.widgetEvents.swipeEnd, index);
      }, 800);
    }

    /**
     * Calculate current index of child element being displayed
     * @returns {number}
     * @private
     */
    _getIndex() {
      let scrollRatio = 0;
      if (this._swipeOpts?.orientation === "x") {
        scrollRatio = this._widgetContainer.scrollWidth > 0 ? Math.abs(this._widgetContainer.scrollLeft) / this._widgetContainer.scrollWidth : 0;
      } else {
        scrollRatio = this._widgetContainer.scrollHeight > 0 ? this._widgetContainer.scrollTop / this._widgetContainer.scrollHeight : 0;
      }

      return Math.round(scrollRatio * this._widget.getVisibleChildrenCount());
    }

    /**
     * Update sibling element during swipe process accordingly to current direction
     * @param direction -1 if going to previous sibling element. 1 if going to next sibling element.
     * @private
     */
    _updateDom(direction) {
      const index = this._getIndex();

      const next = this._widget.getVisibleChildren()[index + direction];
      const previous = this._widget.getVisibleChildren()[index - direction];
      if (previous) {
        previous.removeFromDom();
      }
      if (next) {
        next.addInDom();
      }
    }

    /**
     * Remove siblings elements of the DOM to only display current displayed one
     * @returns {number}
     * @private
     */
    _cleanDom(index) {
      this._pagesInDomTimer = null;
      if (this._widgetContainer) {
        index = index >= 0 ? index : this._getIndex();
        if (this._pastCleanedIndex !== index) {
          if (this._swipeOpts?.virtualDom) {
            for (let i = 0; i < this._widget.getVisibleChildren().length; i++) {
              const child = this._widget.getVisibleChildren()[i];
              if (i === index) {
                child.addInDom();
              } else {
                child.removeFromDom();
              }
            }
          }

          if (this._swipeOpts?.arrows) {
            this._updateArrows(index);
          }
          if (this._swipeOpts?.dots) {
            this._updateDots(index);
          }
          this._pastCleanedIndex = index;
        }
      }
      return index;
    }

    /**
     * Focus the corresponding child manually
     * @param {classes.WidgetBase} child
     * @param {Object} opts swipe options
     * @param {boolean} opts.smoothEffect internal app hash
     * @param {boolean} opts.noDelay to remove delayed scroll
     */
    swipeTo(child, opts) {
      // 1. check if corresponding child is in dom, otherwise add it
      opts = opts || {};
      if (child) {
        child.addInDom();
      }

      // 2. scroll with/without smooth effect
      const index = this._widget.getVisibleChildren().indexOf(child);

      if (this._postponedScroll) {
        this._postponedScroll = null;
      }
      if (this._scrollTimerId) {
        this._clearTimeout(this._scrollTimerId);
        this._scrollTimerId = null;
      }
      const schedulerService = context.SessionService.getCurrent().getCurrentApplication().scheduler;
      // check if nothing to process before scrolling (and thus focusing)
      if (schedulerService.hasNoCommandToProcess()) {
        if (opts.noDelay === true) {
          this._registerAnimationFrame(() => {
            //Need to wait the next frame to have a correct scrollWidth
            this._scroll(index, opts.smoothEffect);
          });
        } else {
          this._scrollTimerId = this._registerTimeout(() => {
            this._scroll(index, opts.smoothEffect);
          }, 100);
        }
      } else {
        this._postponedScroll = this._scroll.bind(this, index, opts.smoothEffect);
      }
    }

    /**
     * Generate a scroll to the specified index
     * @param {Number} index
     * @param {boolean} smoothEffect
     * @returns {cancel}
     * @private
     */
    _scroll(index, smoothEffect) {
      if (!this._widgetContainer || this._destroyed || !this._swipeOpts) {
        return;
      }
      if (this._swipeOpts.arrows) {
        this._updateArrows(index);
      }
      if (this._swipeOpts.dots) {
        this._updateDots(index);
      }
      let left =
        (this._swipeOpts?.orientation === "x" ? this._widgetContainer.scrollWidth : this._widgetContainer.scrollHeight) *
        (index / this._widget.getVisibleChildren().length);
      if (this._widget.isReversed()) {
        // manage RTL mode
        left *= -1;
      }
      this._widgetContainer.classList.remove("visibility-hidden");
      if (this._hasNativeSmoothScroll || !smoothEffect) {
        const scrollParams = {
          [this._swipeOpts?.orientation === "x" ? "left" : "top"]: left
        };
        if (smoothEffect) {
          scrollParams.behavior = "smooth";
        }
        this._widgetContainer.scrollTo(scrollParams);
      } else {
        // IOS
        this._smoothScrollPolyfill(this._swipeOpts?.orientation === "x" ? "scrollLeft" : "scrollTop", left);
      }
    }

    /**
     * Update dom & arrows/dot on focus restored or eventually execute a postponed scroll operation
     * @private
     */
    _onFocusRestored(notifier, event) {
      if (this._postponedScroll) {
        this._postponedScroll();
        this._postponedScroll = null;

        this._onFocus(notifier, event);
      }

      if (this._swipeOpts.arrows) {
        this.refreshArrows();
      }

      if (this._swipeOpts.dots) {
        this.refreshDots();
      }
    }

    /**
     * Update dom & arrows/dot on set focus
     * @private
     */
    _onFocus(notifier, event) {
      let index = -1;
      if (event && event._focusedNode) {
        let child = event._focusedNode.getController().getWidget();
        while (index === -1 && child) {
          index = this._widget.getVisibleChildren().indexOf(child);
          child = child.getParentWidget();
        }
      }
      this._registerTimeout(this._cleanDom.bind(this, index), 200);
    }

    // NAVIGATION STYLE
    /**
     * Add arrows as helper to swipe
     */
    addArrows() {
      this._swipeOpts.arrows = true;

      if (this._leftArrow && this._rightArrow) {
        // arrows already enabled
        return;
      }
      this._leftArrow = document.createElement("div");
      this._rightArrow = document.createElement("div");
      this._leftArrow.classList.add("left_arrow");
      this._rightArrow.classList.add("right_arrow");
      this._widgetContainer.parentNode.appendChild(this._leftArrow);
      this._widgetContainer.parentNode.appendChild(this._rightArrow);

      // add left/right arrows events
      cls.DOMElementHelper.on(this._leftArrow, "click.LeftArrow", this._arrowHandler.bind(this, -1));
      cls.DOMElementHelper.on(this._rightArrow, "click.RightArrow", this._arrowHandler.bind(this, 1));

      this._updateArrows(0);

      if (!this._focusHandler) {
        this._focusHandler = this._widget.getUserInterfaceWidget().when(context.constants.widgetEvents.splitViewChange, this._onFocus.bind(this));
      }
    }

    /**
     * true if arrows are enabled
     * @returns {boolean}
     */
    hasArrows() {
      return this._swipeOpts.arrows;
    }

    /**
     * true if dots are enabled
     * @returns {boolean}
     */
    hasDots() {
      return this._swipeOpts.dots;
    }

    /**
     * Remove arrows buttons
     */
    removeArrows() {
      this._swipeOpts.arrows = false;
      if (!this._leftArrow && !this._rightArrow) {
        // arrows already disabled
        return;
      }
      cls.DOMElementHelper.off(this._leftArrow, "click.LeftArrow");
      cls.DOMElementHelper.off(this._rightArrow, "click.RightArrow");
      this._leftArrow.remove();
      this._rightArrow.remove();
      this._leftArrow = null;
      this._rightArrow = null;

      if (!this._swipeOpts.dots) {
        if (this._focusRestoredHandler) {
          this._focusRestoredHandler();
          this._focusRestoredHandler = null;
        }
        if (this._focusHandler) {
          this._focusHandler();
          this._focusHandler = null;
        }
      }
    }

    /**
     * Arrows buttons
     * @param {Number} index
     * @private
     */
    _updateArrows(index) {
      if (this._leftArrow && this._rightArrow) {
        if (this._widget.getVisibleChildrenCount() === 1) {
          this._leftArrow.classList.add("disabled");
          this._rightArrow.classList.add("disabled");
        } else if (index <= 0) {
          this._leftArrow.classList.add("disabled");
          this._rightArrow.classList.remove("disabled");
        } else if (index >= this._widget.getVisibleChildrenCount() - 1) {
          this._rightArrow.classList.add("disabled");
          this._leftArrow.classList.remove("disabled");
        } else {
          this._rightArrow.classList.remove("disabled");
          this._leftArrow.classList.remove("disabled");
        }
      }
    }

    /**
     * Navigate to previous/next view
     * @param {Number} inc
     * @private
     */
    _arrowHandler(inc) {
      const newIndex = this._getIndex() + inc;
      const child = this._widget.getVisibleChildren()[newIndex];
      if (child) {
        this._onSwipeStart();
        this.swipeTo(child, {
          smoothEffect: true
        });
        this._onSwipeEnd();
      }
    }

    /**
     * Add dots as helper to swipe
     */
    addDots() {
      this._swipeOpts.dots = true;

      if (this._dots) {
        // dots already enabled
        return;
      }
      this._dots = document.createElement("div");

      this._dots.classList.add("dots");

      this._widgetContainer.parentNode.appendChild(this._dots);

      // add dots events
      this._widget.getVisibleChildren().forEach((_child) => this._addDot());

      this._updateDots(0);

      if (!this._focusHandler) {
        this._focusHandler = this._widget.getUserInterfaceWidget().when(context.constants.widgetEvents.splitViewChange, this._onFocus.bind(this));
      }
    }

    removeDots() {
      this._swipeOpts.dots = false;
      if (!this._swipeOpts.arrows) {
        if (this._focusRestoredHandler) {
          this._focusRestoredHandler();
          this._focusRestoredHandler = null;
        }
        if (this._focusHandler) {
          this._focusHandler();
          this._focusHandler = null;
        }
      }

      if (!this._dots) {
        // dots already disabled
        return;
      }
      this._dots.remove();
      for (const element of this._dots.children) {
        cls.DOMElementHelper.off(element, "click.NavigationStyleDot");
      }
      this._dots = null;
    }

    /**
     * Create a dot element and add it in the DOM
     * @private
     */
    _addDot() {
      const div = document.createElement("div");
      div.classList.add("dot");
      cls.DOMElementHelper.on(div, "click.NavigationStyleDot", this._dotHandler.bind(this, this._dots.children.length));
      // add listener
      this._dots.appendChild(div);
    }

    /**
     * Navigate to view corresponding to dot index
     * @param {Number} index
     * @private
     */
    _dotHandler(index) {
      const child = this._widget.getVisibleChildren()[index];
      if (child) {
        this._onSwipeStart();
        this.swipeTo(child, {
          smoothEffect: true
        });
        this._onSwipeEnd();
      }
    }

    /**
     * Update current dot status
     * @param {Number} index
     * @private
     */
    _updateDots(index) {
      // need to resynchronize dots with childs
      if (this._widget.getVisibleChildrenCount() !== this._dots.children.length) {
        this.removeDots();
        this.addDots();
      }
      if (!this._dots) {
        return;
      }
      for (let i = 0; i < this._dots.children.length; i++) {
        const dot = this._dots.children[i];
        if (i === index) {
          if (!dot.classList.contains("current")) {
            dot.classList.add("current");
          }
        } else {
          dot.classList.remove("current");
        }
      }
    }

    /**
     * Test smooth scroll functionality
     * @returns {string|boolean}
     * @private
     */
    _testSupportsSmoothScroll() {
      let supports = false;
      try {
        let div = document.createElement("div");
        div.scrollTo({
          top: 0,
          get behavior() {
            supports = true;
            return "smooth";
          }
        });
      } catch (err) {} // Edge throws an error
      return supports;
    }

    /**
     * Quintic easing out curve function.
     * Creates a smooth deceleration at the end of an animation.
     *
     * @param {number} x - Unused (can be removed, sometimes used by libraries).
     * @param {number} t - Current time (how far into the animation we are).
     * @param {number} b - Start value (beginning position/scale/etc).
     * @param {number} c - Change in value (end - start).
     * @param {number} d - Duration of the animation.
     * @returns {number} - The interpolated value at time `t`.
     */
    _makeEaseOutQuint(x, t, b, c, d) {
      t = t / d - 1; // Normalize time to [0, 1], then shift so it starts at -1
      return c * (t ** 5 + 1) + b; // Quintic polynomial easing
    }

    _smoothScrollPolyfill(key, target) {
      const startTime = Date.now();
      const offset = this._widgetContainer[key];
      const gap = target - offset;
      const duration = 1000;
      let interrupt = false;

      const cancel = () => {
        interrupt = true;
        this._widgetContainer.removeEventListener("wheel", cancel);
        this._widgetContainer.removeEventListener("touchstart", cancel);
      };

      const step = () => {
        const elapsed = Date.now() - startTime;
        const percentage = elapsed / duration;

        if (interrupt) {
          return;
        }

        if (percentage > 1) {
          this._widgetContainer.removeEventListener("wheel", cancel);
          this._widgetContainer.removeEventListener("touchstart", cancel);
          return;
        }

        this._widgetContainer[key] = this._makeEaseOutQuint(0, elapsed, offset, gap, duration);
        window.requestAnimationFrame(step);
      };

      this._widgetContainer.addEventListener("wheel", cancel, {
        passive: true
      });
      this._widgetContainer.addEventListener("touchstart", cancel, {
        passive: true
      });

      step();

      return cancel;
    }

    _smoothHorizontalScrolling(e, time, amount, start) {
      const eAmt = amount / 100;
      let curTime = 0;
      let scrollCounter = 0;
      while (curTime <= time) {
        window.setTimeout(this._SHS_B, curTime, e, scrollCounter, eAmt, start);
        curTime += time / 100;
        scrollCounter++;
      }
    }

    _SHS_B(e, sc, eAmt, start) {
      e.scrollLeft = eAmt * sc + start;
    }

    refreshDots() {
      if (!this.hasDots()) {
        return;
      }
      this._updateDots(this._getIndex());
    }

    refreshArrows() {
      if (!this.hasArrows()) {
        return;
      }
      this._updateArrows(this._getIndex());
    }
  }

  cls.GestureService = GestureService;
  cls.ApplicationServiceFactory.register("Gesture", cls.GestureService);
});
